import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { apiGet } from '../lib/api';

interface ActivityMap {
  [date: string]: { meetings: number; tasks: number; suggested?: number };
}

function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLocalToday(): string {
  return formatDateKey(new Date());
}

/** Get the start of the week containing `date`, using the given weekStartDay (0=Sun, 1=Mon). */
function getWeekStart(date: Date, weekStartDay: number): Date {
  const d = new Date(date);
  const diff = (d.getDay() - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

/** Get array of 7 dates for the week containing `date`. */
function getWeekDays(date: Date, weekStartDay: number): Date[] {
  const start = getWeekStart(date, weekStartDay);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/** Get a 6×7 grid of dates for the given month. */
function getMonthGrid(year: number, month: number, weekStartDay: number): Date[][] {
  const firstOfMonth = new Date(year, month, 1);
  const start = getWeekStart(firstOfMonth, weekStartDay);
  const rows: Date[][] = [];
  const current = new Date(start);
  for (let row = 0; row < 6; row++) {
    const week: Date[] = [];
    for (let col = 0; col < 7; col++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    rows.push(week);
  }
  return rows;
}

const DAY_LABELS_SUN = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_LABELS_MON = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Expanded view (#309): the day grid, a year's months, or a page of years - as iOS Calendar zooms out. */
type View = 'days' | 'months' | 'years';
const YEARS_PER_PAGE = 12;
/** First year of the 12-year page holding `year`. */
export function yearPageStart(year: number): number {
  return year - (((year % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);
}

/** What the expanded panel shows: a view and where it is. */
interface Pane { view: View; year: number; month: number; page: number }
/** How the panel last changed (#312): sideways to the next/previous page, or zooming out/in a level. */
type Motion = 'next' | 'prev' | 'out' | 'in';
const ENTER: Record<Motion, string> = { next: 'cal-in-right', prev: 'cal-in-left', out: 'cal-enter-big', in: 'cal-enter-small' };
const EXIT: Record<Motion, string> = { next: 'cal-out-left', prev: 'cal-out-right', out: 'cal-exit-small', in: 'cal-exit-big' };
/** A little longer than the CSS animation, so the outgoing pane is gone even if animationend never fires. */
const LEAVE_MS = 260;

export function CalendarWidget() {
  const navigate = useNavigate();
  const { prefs } = usePreferences();
  const showWidget = prefs['sidebar.calendarWidget'] !== 'false';
  const weekStartDay = parseInt(prefs['sidebar.weekStartDay'] || '0', 10);
  const dayLabels = weekStartDay === 1 ? DAY_LABELS_MON : DAY_LABELS_SUN;

  const [expanded, setExpanded] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [view, setView] = useState<View>('days');
  const [yearPage, setYearPage] = useState(() => yearPageStart(new Date().getFullYear()));
  const [activity, setActivity] = useState<ActivityMap>({});
  // The pane being animated out, and a counter that remounts the incoming pane so its animation runs (#312).
  const [leaving, setLeaving] = useState<{ pane: Pane; motion: Motion } | null>(null);
  const [motion, setMotion] = useState<Motion | null>(null);
  const [seq, setSeq] = useState(0);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const today = getLocalToday();

  const weekDays = useMemo(() => getWeekDays(new Date(), weekStartDay), [today, weekStartDay]);

  const monthGrid = useMemo(
    () => getMonthGrid(viewMonth.year, viewMonth.month, weekStartDay),
    [viewMonth.year, viewMonth.month, weekStartDay]
  );

  // Compute the date range needed for fetching activity data
  const fetchRange = useMemo(() => {
    if (expanded) {
      const firstDay = monthGrid[0][0];
      const lastDay = monthGrid[monthGrid.length - 1][6];
      return { from: formatDateKey(firstDay), to: formatDateKey(lastDay) };
    }
    return { from: formatDateKey(weekDays[0]), to: formatDateKey(weekDays[6]) };
  }, [expanded, monthGrid, weekDays]);

  // Fetch activity data
  useEffect(() => {
    if (!showWidget) return;
    let cancelled = false;
    apiGet<ActivityMap>(`/calendar/activity?from=${fetchRange.from}&to=${fetchRange.to}`)
      .then(data => { if (!cancelled) setActivity(prev => ({ ...prev, ...data })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fetchRange.from, fetchRange.to, showWidget]);

  const handleDayClick = useCallback((dateKey: string) => {
    navigate(`/day/${dateKey}`);
  }, [navigate]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setExpanded(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setExpanded(false); setView('days'); setLeaving(null); setMotion(null);
    }, 200);
  }, []);

  useEffect(() => () => { clearTimeout(hoverTimeoutRef.current); clearTimeout(leaveTimerRef.current); }, []);

  const current: Pane = { view, year: viewMonth.year, month: viewMonth.month, page: yearPage };

  /** Apply a change with the old pane sliding or zooming out while the new one comes in (#312). */
  function transition(m: Motion, apply: () => void) {
    setLeaving({ pane: current, motion: m });
    setMotion(m);
    setSeq(n => n + 1);
    apply();
    clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => setLeaving(null), LEAVE_MS);
  }

  // Collapsed, the title opens the month; open, it zooms out to the year's months (#309).
  function handleTitleClick() {
    if (!expanded) { setExpanded(true); return; }
    transition('out', () => setView('months'));
  }

  /** Back to this month's day grid, from wherever the panel is (#314). */
  function resetToThisMonth() {
    const now = new Date();
    const target = { year: now.getFullYear(), month: now.getMonth() };
    const diff = (target.year - viewMonth.year) * 12 + (target.month - viewMonth.month);
    if (view !== 'days' || diff !== 0) {
      transition(view !== 'days' ? 'in' : diff > 0 ? 'next' : 'prev', () => { setViewMonth(target); setView('days'); });
    }
  }

  // Today opens today's page, like clicking its date, and brings the grid back to it (#309).
  function goToToday() {
    resetToThisMonth();
    navigate(`/day/${today}`);
  }

  /** Leave a picker for the view it was opened from, picking nothing (#314). */
  function goBack() {
    transition('in', () => setView(view === 'years' ? 'months' : 'days'));
  }

  function step(delta: number) {
    transition(delta > 0 ? 'next' : 'prev', () => {
      if (view === 'months') { setViewMonth(prev => ({ ...prev, year: prev.year + delta })); return; }
      if (view === 'years') { setYearPage(prev => prev + delta * YEARS_PER_PAGE); return; }
      setViewMonth(prev => {
        const m = prev.month + delta;
        if (m < 0) return { year: prev.year - 1, month: 11 };
        if (m > 11) return { year: prev.year + 1, month: 0 };
        return { year: prev.year, month: m };
      });
    });
  }

  function openYears() {
    transition('out', () => { setYearPage(yearPageStart(viewMonth.year)); setView('years'); });
  }

  function pickYear(year: number) {
    transition('in', () => { setViewMonth(prev => ({ ...prev, year })); setView('months'); });
  }

  function pickMonth(month: number) {
    transition('in', () => { setViewMonth(prev => ({ ...prev, month })); setView('days'); });
  }

  const nowMonth = new Date().getMonth();
  const nowYear = new Date().getFullYear();
  // Collapsed, the strip is this week, so the title names this month; open, the month being browsed (#314).
  const shown = expanded ? viewMonth : { year: nowYear, month: nowMonth };
  const awayFromNow = view !== 'days' || viewMonth.year !== nowYear || viewMonth.month !== nowMonth;
  // Bottom-anchored: the title sits low in its row (inline-block line box), and bottom-1 matches its mb-1.
  const cornerCls = 'absolute bottom-1 p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors';

  if (!showWidget) return null;

  function DayCell({ date, isCurrentMonth = true }: { date: Date; isCurrentMonth?: boolean }) {
    const dateKey = formatDateKey(date);
    const isToday = dateKey === today;
    const isPast = dateKey < today;
    const act = activity[dateKey];
    const hasDot = act && (act.meetings > 0 || act.tasks > 0);
    const hasSuggested = act && (act.suggested ?? 0) > 0 && !hasDot;

    return (
      <button
        onClick={() => handleDayClick(dateKey)}
        className={`
          relative flex flex-col items-center justify-center w-full aspect-square rounded-md text-[10px] leading-none transition-colors
          ${isToday ? 'bg-accent text-white font-bold' : ''}
          ${!isToday && isPast ? 'text-text-muted' : ''}
          ${!isToday && !isPast ? 'text-text-secondary' : ''}
          ${!isCurrentMonth ? 'opacity-30' : ''}
          hover:bg-bg-hover
        `}
      >
        {date.getDate()}
        {hasDot && (
          <span className={`absolute bottom-0.5 w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-accent'}`} />
        )}
        {hasSuggested && (
          <span data-testid="suggested-dot" className={`absolute bottom-0.5 w-1 h-1 rounded-full border bg-transparent ${isToday ? 'border-white' : 'border-accent'}`} />
        )}
      </button>
    );
  }

  function renderPane(p: Pane) {
    if (p.view === 'months') {
      return (
        <div className="grid h-full grid-cols-3 grid-rows-4 gap-1" data-testid="month-picker">
          {MONTH_NAMES.map((name, m) => {
            const current = p.year === nowYear && m === nowMonth;
            return (
              <button key={name} onClick={() => pickMonth(m)}
                aria-current={current ? 'date' : undefined}
                className={`rounded-md text-[10px] transition-colors hover:bg-bg-hover ${current ? 'bg-accent text-white font-bold' : m === p.month ? 'text-accent font-semibold' : 'text-text-secondary'}`}>
                {name}
              </button>
            );
          })}
        </div>
      );
    }
    if (p.view === 'years') {
      return (
        <div className="grid h-full grid-cols-3 grid-rows-4 gap-1" data-testid="year-picker">
          {Array.from({ length: YEARS_PER_PAGE }, (_, i) => p.page + i).map(y => (
            <button key={y} onClick={() => pickYear(y)}
              aria-current={y === nowYear ? 'date' : undefined}
              className={`rounded-md text-[10px] transition-colors hover:bg-bg-hover ${y === nowYear ? 'bg-accent text-white font-bold' : y === p.year ? 'text-accent font-semibold' : 'text-text-secondary'}`}>
              {y}
            </button>
          ))}
        </div>
      );
    }
    return (
      <>
        <div className="grid grid-cols-7 gap-0.5 mb-0.5">
          {dayLabels.map((label, i) => (
            <span key={i} className="text-center text-[8px] text-text-muted font-medium">
              {label}
            </span>
          ))}
        </div>
        {getMonthGrid(p.year, p.month, weekStartDay).map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-0.5">
            {week.map(d => (
              <DayCell key={formatDateKey(d)} date={d} isCurrentMonth={d.getMonth() === p.month} />
            ))}
          </div>
        ))}
      </>
    );
  }

  return (
    <div
      ref={containerRef}
      className="px-2 py-2"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Month/Year label, with back and reset in the corners (#314) */}
      <div className="relative">
      {view === 'days' && (
        <button
          onClick={handleTitleClick}
          aria-label={expanded ? `Choose a month (${MONTH_NAMES[shown.month]} ${shown.year})` : undefined}
          className="w-full text-center text-[10px] font-medium uppercase tracking-wider text-text-muted mb-1 hover:text-text-secondary transition-colors cursor-pointer"
        >
          {MONTH_NAMES[shown.month]} {shown.year}
        </button>
      )}
      {view === 'months' && (
        <button
          onClick={openYears}
          aria-label={`Choose a year (${viewMonth.year})`}
          className="w-full text-center text-[10px] font-medium uppercase tracking-wider text-text-muted mb-1 hover:text-text-secondary transition-colors cursor-pointer"
        >
          {viewMonth.year}
        </button>
      )}
      {view === 'years' && (
        // Inline-block like the title buttons, so the panel keeps the same height in this view too (#312).
        <div className="inline-block w-full text-center text-[10px] font-medium uppercase tracking-wider text-text-muted mb-1">
          {yearPage} - {yearPage + YEARS_PER_PAGE - 1}
        </div>
      )}
      {expanded && view !== 'days' && (
        <button onClick={goBack} aria-label={view === 'years' ? 'Back to months' : 'Back to days'} title="Back"
          className={`${cornerCls} left-0`}>
          <ArrowLeft size={10} />
        </button>
      )}
      {expanded && awayFromNow && (
        <button onClick={resetToThisMonth} aria-label="Back to this month" title="This month"
          className={`${cornerCls} right-0`}>
          <RotateCcw size={10} />
        </button>
      )}
      </div>

      {/* Week strip (always visible) */}
      {!expanded && (
        <div>
          <div className="grid grid-cols-7 gap-0.5 mb-0.5">
            {dayLabels.map((label, i) => (
              <span key={i} className="text-center text-[8px] text-text-muted font-medium">
                {label}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {weekDays.map(d => (
              <DayCell key={formatDateKey(d)} date={d} />
            ))}
          </div>
        </div>
      )}

      {/* Expanded month view */}
      {expanded && (
        <div className="cal-anim cal-drop">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => step(-1)} aria-label={view === 'days' ? 'Previous month' : view === 'months' ? 'Previous year' : 'Earlier years'} className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors">
              <ChevronLeft size={12} />
            </button>
            <button onClick={goToToday} className="text-[9px] text-accent hover:text-accent-hover transition-colors">
              Today
            </button>
            <button onClick={() => step(1)} aria-label={view === 'days' ? 'Next month' : view === 'months' ? 'Next year' : 'Later years'} className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors">
              <ChevronRight size={12} />
            </button>
          </div>

          {/* Every view sits on a box the day grid's size, so the panel never shrinks out from under the mouse (#312). */}
          <div className="relative overflow-hidden" data-testid="calendar-stage">
            <div aria-hidden="true" className="invisible" data-testid="calendar-sizer">
              <div className="grid grid-cols-7 gap-0.5 mb-0.5"><span className="text-center text-[8px] font-medium">&nbsp;</span></div>
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="grid grid-cols-7 gap-0.5"><span className="w-full aspect-square" /></div>
              ))}
            </div>
            {leaving && (
              <div key={`leaving-${seq}`} aria-hidden="true" inert data-testid="calendar-leaving"
                className={`absolute inset-0 pointer-events-none cal-anim cal-leaving ${EXIT[leaving.motion]}`}
                onAnimationEnd={() => setLeaving(null)}>
                {renderPane(leaving.pane)}
              </div>
            )}
            <div key={`pane-${seq}`} data-testid="calendar-pane" data-motion={motion ?? undefined}
              className={`absolute inset-0 ${motion ? `cal-anim ${ENTER[motion]}` : ''}`}>
              {renderPane(current)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
